import React, { useEffect, useState, useMemo } from "react";
import {
  faEdit,
  faMagnifyingGlass,
  faShare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Swal from "sweetalert2";

import { useRecoilState, useSetRecoilState } from "recoil";
import { loadingState } from "global/LoaderState";
import debounce from "lodash.debounce";
import Modal from "utils/Modal";

import { AlertState } from "global/AlertState";
import AddEmployee from "./AddEmployee";
import AddDelinquency from "../delinquency/AddDelinquency_org";
import callApi from "services/api";
import { UserState } from "global/UserState";
import { formatDate } from "utils/DateFormatter";
import { getMasterdata } from "../delinquency/apis";

const Employee = () => {
  const [showModal, setShowModal] = useState(false);
  const [user] = useRecoilState(UserState);
  const [editingData, setEditingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loader = useSetRecoilState(loadingState);
  const [empList, setEmpList] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [unitList, setUnitList] = useState([]);
  const [subUnitList, setSubUnitList] = useState([]);
  const limit = 10;

  // transfer
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");
  const [remark, setRemark] = useState("");
  const [transferError, setTransferError] = useState("");
  // const [loading, setLoading] = useState(false);

  const handleOpenModal = (data = null) => {
    setEditingData(data);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setEditingData(null);
    setShowModal(false);
  };

  const getList = async (params) => {
    try {
      const response = await callApi(
        `filtermaster?${params}`,
        "GET",
        null,
        true,
        true,
        (msg) => setError(msg),
        (path) => console.log("navigate to", path),
      );

      if (response.success) {
        return response.body.data;
      }
    } catch (err) {
      setError("Failed to fetch ");
    }
  };

  const getEmployeeList = async (search, page = 1) => {
    try {
      loader(true);
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        search: search || "",
        search_field: "name",
        page: page.toString(),
        limit: limit.toString(),
      });

      const response = await callApi(
        `employee/?${params}`,
        "GET",
        null,
        true,
        true,
        (msg) => setError(msg),
        (path) => console.log("navigate to", path),
      );

      if (response.success) {
        const body = response?.body || {};
        const employees = response?.body?.data || [];

        setCurrentPage(body.current_page || 1);
        setTotalPages(body.total_pages || 1);
        setTotalRecords(body.total_count || 0);

        const enrichedEmployees = await Promise.all(
          employees?.map(async (emp) => {
            const rankData = emp.rank ? await getMasterdata(emp.rank) : null;

            let postingName = "";

            // ✅ Correct Posting Name Logic
            if (emp.posting === "OTHER") {
              postingName = emp.posting_other || "OTHER";
            } else if (emp.posting) {
              const postingData = await getMasterdata(emp.posting);
              postingName = postingData?.name || emp.posting;
            }

            return {
              ...emp,
              
              rankName: rankData?.name || emp.rank,
              postingName: postingName,
            };
          }),
        );

        setEmpList(enrichedEmployees);
      }
    } catch (err) {
      setError(err.message || "Failed to load employees");
    } finally {
      loader(false);
      setLoading(false);
    }
  };

  const debouncedGetEmployeeList = useMemo(
    () =>
      debounce((query, page) => {
        getEmployeeList(query, page);
      }, 300),
    [],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setCurrentPage(1);
    debouncedGetEmployeeList(value, 1);
  };

  const renderRows = () => {
    if (!empList || empList.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="p-3 text-start">
            No Data
          </td>
        </tr>
      );
    }

    return empList?.map((item) => {
      const isTransferred = item.transfer === true;

      return (
        <tr
          key={item.id}
          style={{
            opacity: isTransferred ? 0.5 : 1,
            pointerEvents: isTransferred ? "none" : "auto",
          }}
        >
          <td className="text-center">{item.empCode}</td>
          <td className="text-center">{item.name}</td>
          {/* dob: formatDate(emp.dob) */}
          <td className="text-center">{formatDate(item.dob)}</td>
          <td className="text-center">{item.rankName}</td>
          <td className="text-center">{item.beltNumber}</td>
          <td className="text-center">{item.postingName}</td>

          <td className="text-center">
            {isTransferred ? (
              <span className="text-warning fw-bold">Transfer in Progress</span>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleOpenModal(item)}
                  className="btn btn-sm btn-link me-2"
                  style={{ textDecoration: "none" }}
                >
                  <FontAwesomeIcon icon={faEdit} className="me-1" />
                  Edit
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenTransferModal(item)}
                  className="btn btn-sm btn-success me-2"
                  style={{ textDecoration: "none" }}
                >
                  <FontAwesomeIcon icon={faShare} className="me-1" />
                  Transfer
                </button>
              </>
            )}
          </td>
        </tr>
      );
    });
  };

  const getUnitList = async (id) => {
    const params = new URLSearchParams({
      filter: id
        ? JSON.stringify({ parentId: id })
        : JSON.stringify({ type: "unit" }),
      sort_order: "1",
      page: "1",
      limit: "1000",
    });
    const data = await getList(params);
    if (id) {
      setSubUnitList(data);
    } else {
      setUnitList(data);
    }
  };

  useEffect(() => {
    getEmployeeList("", 1);
    getUnitList();
  }, []);

  // transfer model

  const handleOpenTransferModal = (emp) => {
    setSelectedEmployee(emp);
    setSelectedUnit("");
    setTransferError("");
    setShowTransferModal(true);
  };

  const handleCloseTransferModal = () => {
    setShowTransferModal(false);
    setSelectedEmployee(null);
  };

  const handleTransferSubmit = async (e) => {
    // Validate inputs
    if (!selectedUnit) {
      setTransferError("Please select a unit");
      return;
    }

    const toUnit =
      unitList.find((unit) => unit.id === selectedUnit)?.name ||
      "Selected Unit";
    // Confirm transfer using Swal.fire
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to transfer this employee from ${user?.unit} to ${toUnit}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#33B36B",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Transfer",
      cancelButtonText: "No, Cancel",
    });

    if (!result.isConfirmed) {
      handleCloseTransferModal();
      return;
    }

    setLoading(true);

    try {
      // ✅ Prepare the request body
      const requestBody = {
        to_id: selectedUnit,
        employee_id: selectedEmployee?.id,
        status: "raised",
        remark: remark,
      };

      const response = await callApi(
        "transfer/",
        "POST",
        requestBody,
        true,
        true,
        (msg) => setError(msg),
        (path) => console.log("navigate to", path),
      );

      if (response.success) {
        setTransferData((prevData) => [...prevData, response.body.data]);

        await Swal.fire({
          title: "Success!",
          text: "Transfer created successfully!",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        handleCloseTransferModal();
        setSelectedUnit("");
        setRemark("");
        getEmployeeList("", 1);
        getUnitList();
      }
    } catch (err) {
      console.error("Transfer error:", err);
      setTransferError("Failed to create transfer. Please try again.");

      await Swal.fire({
        title: "Error!",
        text: "Failed to create transfer. Please try again.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="ms-3 me-3 mt-3 d-flex justify-content-between bg-dark rounded ">
        <h5 className="card-title  p-3 m-0 text-white">Employee</h5>
        <h6 className="card-title p-3 mt-1 text-white">
          Department : {user.unit || ""}
          {user.subUnit ? ` | ${user.subUnit}` : ""}
          {user.id}
        </h6>

        {["SUBUNITADMIN", "UNITADMIN"].includes(user?.role) && (
          <button
            type="button"
            className="btn btn-sm text-white me-2"
            onClick={() => handleOpenModal()}
            style={{ textDecoration: "none" }}
          >
            <FontAwesomeIcon icon={faEdit} className="me-1" />
            Add Employee
          </button>
        )}
      </div>
      <div className="card-body ms-3">
        <>
          <div className="row mb-3 mt-4 ps-4">
            <form className="form-inline d-flex">
              <div className="col-md-4">
                <div
                  className="position-relative w-100"
                  style={{ maxWidth: "400px" }}
                >
                  {/* Search Icon */}
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                    style={{ pointerEvents: "none" }} // icon won’t block clicks
                  />
                  <input
                    type="text"
                    name="questionName"
                    onChange={handleSearchChange}
                    placeholder="Type a employee name for search"
                    className="form-control ps-5 py-2"
                    style={{
                      borderRadius: "0.5rem",
                      boxShadow: "0 0 5px rgba(0,0,0,0.1)",
                    }}
                  />
                </div>
              </div>
            </form>
          </div>
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th className="text-center">Unique ID</th>
                  <th className="text-center">Name</th>
                  <th className="text-center">Date Of Birth</th>
                  <th className="text-center">Rank</th>
                  <th className="text-center">Belt Number</th>
                  <th className="text-center">Posting</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Page {currentPage} of {totalPages} | Total Records: {totalRecords}
            </div>

            <div>
              <button
                disabled={currentPage === 1}
                onClick={() => getEmployeeList(search, currentPage - 1)}
                className="btn btn-sm btn-outline-primary me-2"
              >
                Previous
              </button>

              <button
                disabled={currentPage === totalPages}
                onClick={() => getEmployeeList(search, currentPage + 1)}
                className="btn btn-sm btn-outline-primary"
              >
                Next
              </button>
            </div>
          </div>

          <AddEmployee
            showModal={showModal}
            fetchEmployees={getEmployeeList}
            handleCloseModal={handleCloseModal}
            editingData={editingData}
          />
        </>
      </div>
      <Modal
        showModal={showTransferModal}
        handleCloseModal={handleCloseTransferModal}
        title="Transfer Employee"
      >
        <div className="mb-3">
          <label className="form-label">Current Unit</label>
          <input
            type="text"
            className="form-control"
            value={user?.unit || "N/A"}
            disabled
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Select Transfer Unit</label>
          <select
            className="form-control"
            value={selectedUnit}
            onChange={(e) => {
              setSelectedUnit(e.target.value);
              setTransferError("");
            }}
            disabled={loading}
          >
            <option value="">-- Select Unit --</option>
            {unitList
              ?.filter((unit) => unit.name !== user?.unit) // Filter out current unit
              .map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
          </select>
          {transferError && (
            <small className="text-danger">{transferError}</small>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label">Remark</label>
          <textarea
            className="form-control"
            rows="3"
            placeholder="Enter transfer order number and other relevent details."
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            disabled={loading}
          />
        </div>
        <div className="alert alert-info">
          <strong>Employee:</strong> {selectedEmployee?.name || "N/A"}
          <br />
          <strong>Unique ID:</strong> {selectedEmployee?.empCode || "N/A"}
          <br />
          <strong>Current Unit:</strong> {user.unit || "N/A"}
        </div>

        <div className="d-flex justify-content-end mt-3">
          <button
            className="btn btn-secondary me-2"
            onClick={handleCloseTransferModal}
            disabled={loading}
          >
            Close
          </button>
          <button
            className="btn btn-success"
            onClick={handleTransferSubmit}
            disabled={loading}
          >
            {loading ? "Processing..." : "Submit Transfer"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Employee;
